/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {BehaviorSubject, EMPTY, forkJoin, NEVER, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {ProfileMode, REQUEST_OPTIONS} from '../../../app.constants';
import {EmpVariableAttributes, PayPeriodAttributes} from '../../../models/models.interfaces';
import {ApiService} from '../../../shared/api/api.service';
import {Data} from '../../../shared/api/server-responses.interfaces';
import {EmpVariableDTO, EmpVariableWrapper, FlattenFieldDTO} from '../../../shared/api/GaApi';
import {Value} from '../../../shared/form-generator/form-generator.interfaces';

export interface VariablesAndPayPeriods {
  payPeriods: Data<PayPeriodAttributes>[];
  variables: Map<number, Data<EmpVariableAttributes>>;
}

export type EmpVariableForPayPeriod = Data<EmpVariableAttributes> | Array<Data<EmpVariableAttributes>>;

export interface VariableElementLine {
  name: string;
  editedPayPeriod: EmpVariableForPayPeriod;
  previousPayPeriod: EmpVariableForPayPeriod;
  nextPayPeriod: EmpVariableForPayPeriod;
}

export interface SelectedVariable {
  name: string;
  payPeriodId: number;
  isReadOnly: boolean;
}

export interface VariableElementChanges {
  creations: Data<EmpVariableAttributes>[];
  updates: Data<EmpVariableAttributes>[];
  deletions: Data<EmpVariableAttributes>[];
}

type MassUpdateEmpVariable = EmpVariableWrapper & Pick<
  EmpVariableDTO,
  'isBeginDateLinkedToContract' | 'isEndDateLinkedToContract'
>;

export class Converter {

  static fromDataToDto(item: Data<EmpVariableAttributes>): EmpVariableDTO {
    const atts = item.attributes;
    return {
      name: atts.name,
      transId: atts.transId,
      unitTransId: atts.unitTransId,
      id: atts.id,
      begindate: atts.begindate,
      enddate: atts.enddate,
      value: atts.value,
      empContractId: atts.empContract,
      evtype: atts.evtype,
      comment: atts.comment,
      cliCoreID: atts.cliCoreID,
      fields: Converter.fromValuesToFields(atts.values || {}),
      isBeginDateLinkedToContract: atts.isBeginDateLinkedToContract,
      isEndDateLinkedToContract: atts.isEndDateLinkedToContract
    }
  }

  static fromDataToMassUpdateDto(item: Data<EmpVariableAttributes>): MassUpdateEmpVariable {
    const atts = item.attributes;
    return {
      id: item.id,
      begindate: atts.begindate,
      enddate: atts.enddate,
      name: atts.name,
      value: atts.value,
      cliCoreID: atts.cliCoreID,
      unitTransId: atts.unitTransId,
      transId: atts.transId,
      empContract: atts.empContract,
      evtype: atts.evtype,
      comment: atts.comment,
      readOnly: atts.readOnly,
      entityWithPaySerie: atts.entityWithPaySerie,
      values: atts.values,
      isBeginDateLinkedToContract: atts.isBeginDateLinkedToContract,
      isEndDateLinkedToContract: atts.isEndDateLinkedToContract
    };
  }

  private static fromValuesToFields(input: { [key: string]: Value }): FlattenFieldDTO[] {
    return Object.entries(input).map(([key, value1]) => ({
      name: key,
      type: value1.type as FlattenFieldDTO["type"],
      values: [
        {
          beginDate: '1970-01-01',
          endDate: '3000-01-01',
          value: value1.value
        }
      ]
    }));
  }
}

/**
 * Service that offers access to employee variables (EmpVariable) : time and attendance and variable payroll elements.
 */
@Injectable()
export class EmployeeVariablesService {

  public variableSubject: BehaviorSubject<SelectedVariable> = new BehaviorSubject<SelectedVariable>(null);
  public getSelectedVariable = this.variableSubject.asObservable();

  constructor(private api: ApiService) {
  }

  public listVariableElementTypes(cliRegId: number): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/${cliRegId}/elementVariableTypes`)
      .pipe(
        map((result: any) => {
          result.data.map((item: any) => {
            if (item.attributes.unitTransId === 'Amount') {
              item.attributes.unitTransId = item.attributes.currencyString;
            }

            return item;
          });

          return result;
        })
      );
  }

  public getVEListForMDU(): Observable<any> {
    return this.api.get(`/repository/api/v1/client/repVariables`);
  }

  public listVariableElements(contractId: number, periodBegin: string, periodEnd: string): Observable<any> {
    return this.api.get(`/variable/api/v1/empContract/${contractId}/variables`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd,
          type: 'VARIABLE'
        }
      }
    });
  }

  public listAdjustmentsAddedAfterLastExecution(
    contractId: number,
    periodBegin: string,
    periodEnd: string,
    name: string,
    lastPayrollExecutionDate: string): Observable<any> {
    return this.api.get(`/variable/api/v1/empContract/${contractId}/variables`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd,
          type: 'VARIABLE',
          name,
          lastPayrollExecutionDate
        }
      }
    });
  }

  public getPayPeriods(): Observable<any> {
    return this.api.get(`/payroll/api/v1/payPeriod/`);
  }

  public getPayPeriodsBySeriesId(serieId: number, contractBeginDate: string, contractEndDate: string): Observable<Data<PayPeriodAttributes>> {
    const query = "?page=0&perPage=100&orderBy=begindate&order=desc&search=(" +
      `serie.id==${serieId};` +
      `(` +
      `begindate=ge=${contractBeginDate};` +
      `enddate=le=${contractEndDate},` +
      `enddate=ge=${contractBeginDate}` +
      `)` +
      `)`;
    const url = `/payroll/api/v1/payPeriod${query}`;
    return this.api.get(url);
  }

  public getPayPeriodByDateAndPaySerieDuration(contractDate: string, paySerieDuration: number): Observable<any> {
    return this.api.get(` /payroll/api/v1/pay-period/by-date-and-pay-serie-duration`, {
      requestOptions: {
        params: {
          date: contractDate,
          'pay-serie-duration': paySerieDuration
        }
      }
    });
  }

  public getContractPayPeriodRange(contractId: number, payPeriodId?: number, beginDate?: string,
                                   endDate?: string, profileMode = ProfileMode.CLASSIC): Observable<any> {
    const params = {
      requestOptions: {
        params: <any>{}
      }
    };

    if (payPeriodId) {
      params.requestOptions.params.payPeriodId = payPeriodId;
    }
    if (beginDate) {
      params.requestOptions.params.beginDate = beginDate;
    }
    if (endDate) {
      params.requestOptions.params.endDate = endDate;
    }

    return this.api.get(`/payroll/api/v1/payPeriod/byContract/${contractId}/range?profile-mode=${profileMode}`, params);
  }

  public periodsForSeries(serieId: number): Observable<{ data: Data<PayPeriodAttributes>[] }> {
    const params = {
      requestOptions: {
        params: {
          paySerieId: serieId
        }
      }
    };
    return this.api.get(`/payroll/api/v1/payPeriod/listPayPeriodsBySerieId`, params);
  }

  public getVariableElements(cliReg: number): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/${cliReg}/variables`);
  }

  public saveVariableElements({creations, updates, deletions}: VariableElementChanges): Observable<void> {
    return this.api.post('/variable/api/v1/empVariable/massUpdate', {
      creations: creations.map(Converter.fromDataToMassUpdateDto),
      updates: updates.map(Converter.fromDataToMassUpdateDto),
      deletions: deletions.map(element => element.id)
    });
  }

  public updateVariableElements(elements: Data<EmpVariableAttributes>[]): Observable<any> {
    if (elements.length === 0) {
      return EMPTY;
    }
    return forkJoin(elements.map(e => this.updateVariableElement(e)));
  }

  public updateVariableElement(element: Data<EmpVariableAttributes>): Observable<any> {
    return this.api.put(`/variable/api/v2/variables/${element.id}`, Converter.fromDataToDto(element));
  }

  public createVariableElements(elements: Data<EmpVariableAttributes>[]): Observable<any> {
    if (elements.length === 0) {
      return EMPTY;
    }
    return forkJoin(elements.map(e => this.createVariableElement(e)));
  }

  public createVariableElement(element: Data<EmpVariableAttributes>): Observable<Response> {
    return this.api.post("/variable/api/v2/variables/create", Converter.fromDataToDto(element));
  }

  public deleteVariableElements(elements: Data<EmpVariableAttributes>[]): Observable<any> {
    if (elements.length === 0) {
      return EMPTY;
    }
    return forkJoin(elements.map(e => this.deleteVariableElement(e)));
  }

  public deleteVariableElement(element: Data<EmpVariableAttributes>): Observable<any> {
    return this.api.delete(`/variable/api/v1/empVariable/${element.id}`);
  }

  public getEmployeeVariables(
    id: number,
    periodBegin: string,
    periodEnd: string,
    isEss: boolean = false
  ): Observable<any> {

    return this.api.get(`/variable/api/v1/empContract/${id}/variables`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd,
          type: "TIME",
          isEss,
        },
      },
    });
  }

  public getRepVariableZones(cliRegId: number, name: string, isEss: boolean = false) {
    return this.api.config
      .get(`/cliReg/${cliRegId}/repVariableZones/${name}?isEss=${isEss}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        switchMap((response) => {
          if (response.status === 204) {
            return NEVER;
          }
          return of(response.body);
        })
      );
  }

  public updateSelectedVariable(variable: any) {
    this.variableSubject.next(variable);
  }

  public splitVariablesByRecurrence(
    variableElements: { [name: string]: Data<EmpVariableAttributes>[] },
    visibleVariables: { [name: string]: string }
  ): {
    recurrentVariables: { [name: string]: Data<EmpVariableAttributes>[] },
    nonRecurrentVariables: { [name: string]: Data<EmpVariableAttributes>[] }
  } {
    const recurrentVariables = {};
    const nonRecurrentVariables = {};

    for (const key in variableElements) {
      if (Object.prototype.hasOwnProperty.call(variableElements, key)) {
        const elements = variableElements[key];
        recurrentVariables[key] = [];
        nonRecurrentVariables[key] = [];

        elements.forEach(element => {
          const visibility = visibleVariables[element.attributes.name];
          if (visibility) {
            if (visibility === 'RECURRENT') {
              recurrentVariables[key].push(element);
            } else if (visibility === 'NON_RECURRENT') {
              nonRecurrentVariables[key].push(element);
            }
          }
        });

        // Remove empty arrays
        if (recurrentVariables[key].length === 0) {
          delete recurrentVariables[key];
        }
        if (nonRecurrentVariables[key].length === 0) {
          delete nonRecurrentVariables[key];
        }
      }
    }

    return {recurrentVariables, nonRecurrentVariables};
  }

  public tableHeader(index: number): string {
    const headerTransId = [
      'PAYROLL_PREPARE.recurrent_variables',
      'PAYROLL_PREPARE.non_recurrent_variables'
    ]

    return headerTransId[index];
  }

  public isEmployeeVariablesRecurring(): Observable<boolean> {
    // Retrieves a boolean determining whether the variables will be separated by recurrence type
    return this.api.get('/repository/api/v1/global/offering/recurrence-in-variables', REQUEST_OPTIONS.RESPONSE_TYPE.JSON);
  }

  public getVisibleVariablesRecurrence(cliRegId: number): Observable<{ [key: string]: string }> {
    // Retrieves a key/value object with the recurrence type for each variable
    return this.api.get(`/repository/api/v1/regulation/variables/recurrenceTypes/${cliRegId}`, REQUEST_OPTIONS.RESPONSE_TYPE.JSON);
  }
}
